import { baseUrl } from "../../../baseUrl";
import { Dropdown } from "../../common/dropDown";
import { FolderOpen } from "@mui/icons-material";
import { TextInput } from "../../common/textInput";
import { useMutation } from "@tanstack/react-query";
import { fetchWithAuthHandling } from "../../../authHandling";
import { checkCallAvailability } from "../checkCallAvailability";
import { useState, useEffect, useRef, type ChangeEvent } from "react";
import {
    Box,
    Table,
    Paper,
    Button,
    Dialog,
    TableRow,
    TableHead,
    TableCell,
    TableBody,
    Typography,
    DialogTitle,
    DialogContent,
    TableContainer,
    CircularProgress,
    type SelectChangeEvent,
} from "@mui/material";

interface UploadListModalProps {
    openDialog: boolean;
    setOpenDialog: (open: boolean) => void;
    setOpenUploadErrorDialog: (open: boolean) => void;
}

export const UploadListModal = ({ openDialog, setOpenDialog, setOpenUploadErrorDialog }: UploadListModalProps) => {
    const [fileName, setFileName] = useState("");
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedOffer, setSelectedOffer] = useState<string | number>("");
    const [_, setUploadedFileBase64] = useState<string | null>(null);

    const [fileNameError, setFileNameError] = useState(false);
    const [offerError, setOfferError] = useState(false);
    const [submissionError, setSubmissionError] = useState<string | null>(null);

    const [blockedNumbersCount, setBlockedNumbersCount] = useState<number | null>(null);
    const [availableNumbersCount, setAvailableNumbersCount] = useState<number | null>(null);

    const [isUploading, setIsUploading] = useState(false);
    const [isDisabledAfterSuccess, setIsDisabledAfterSuccess] = useState(false);

    const offers = [
        { label: "Special Discount", value: "offer1" },
        { label: "Limited Time Offer", value: "offer2" },
        { label: "VIP Package", value: "offer3" },
        { label: "Holiday Deal", value: "offer4" }
    ];

    useEffect(() => {
        if (openDialog) {
            const today = new Date().toISOString().split("T")[0];
            setFileName(`${today} Call List`);
            setSelectedOffer("");
            setUploadedFileBase64(null);
            setFileNameError(false);
            setOfferError(false);
            setSubmissionError(null);
            setBlockedNumbersCount(null);
            setAvailableNumbersCount(null);
            setIsUploading(false);
            setIsDisabledAfterSuccess(false);
        }
    }, [openDialog]);

    const handleFileNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setFileName(event.target.value);
        if (event.target.value.trim() !== "") setFileNameError(false);
    };

    const handleOfferChange = (event: SelectChangeEvent<string | number>) => {
        setSelectedOffer(event.target.value);
        if (event.target.value) setOfferError(false);
    };

    const handleDownloadTemplate = () => {
        console.log("Downloading template...");
    };

    const handleUploadFile = () => {
        if (!fileName.trim()) setFileNameError(true);
        if (!selectedOffer) setOfferError(true);
        if (!fileName.trim() || !selectedOffer) return;

        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const response = await checkCallAvailability();
        if (!response) {
            setOpenDialog(false);
            setOpenUploadErrorDialog(true);
            return;
        }
        if (event.target.files && event.target.files.length > 0) {
            const file = event.target.files[0];
            const reader = new FileReader();

            reader.onload = () => {
                if (reader.result) {
                    const base64String = reader.result.toString();
                    setUploadedFileBase64(base64String);

                    console.log("📌 Uploaded File Details:");
                    console.log("📂 Call List Name:", fileName);
                    console.log("🏷️ Selected Offer:", selectedOffer || "No Offer Selected");
                    console.log("🖼️ File Base64:", base64String);

                    setIsUploading(true);
                    submitCallList({ fileName, selectedOffer, fileUrl: base64String });
                }
            };

            reader.readAsDataURL(file);
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const { mutate: submitCallList } = useMutation({
        mutationFn: async ({
            fileName,
            selectedOffer,
            fileUrl
        }: {
            fileName: string;
            selectedOffer: string | number;
            fileUrl: string;
        }) => {
            const response = await fetchWithAuthHandling(`${baseUrl}/calls/upload`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                    name: fileName,
                    offer: selectedOffer,
                    fileUrl
                })
            });

            if (!response.ok) {
                throw new Error("Failed to upload call list. Please try again.");
            }

            return response.json();
        },
        onError: (error) => {
            setSubmissionError(error.message);
            setIsUploading(false);
            setIsDisabledAfterSuccess(false);
        },
        onSuccess: (data) => {
            setBlockedNumbersCount(data.blockedNumbers);
            setAvailableNumbersCount(data.availableNumbers);
            setIsUploading(false);
            setIsDisabledAfterSuccess(true);
        }
    });

    return (
        <Dialog
            open={openDialog}
            onClose={handleCloseDialog}
            fullWidth
            maxWidth="sm"
            aria-labelledby="upload-list-dialog-title"
        >
            <DialogTitle id="upload-list-dialog-title" sx={{ fontWeight: "bold" }}>
                Upload Call List
            </DialogTitle>

            <DialogContent dividers sx={{ p: 3 }}>
                <Box sx={{ mb: 0.5 }}>
                    <TextInput
                        label="Call List Name"
                        value={fileName}
                        onChange={handleFileNameChange}
                        placeholder="YYYY-MM-DD Call List"
                        error={fileNameError}
                        helperText={fileNameError ? "Call List Name is required." : ""}
                        disabled={isUploading || isDisabledAfterSuccess}
                    />
                </Box>

                <Dropdown
                    label="Offer"
                    value={selectedOffer}
                    options={offers}
                    onChange={handleOfferChange}
                    helperText={offerError ? "Please select an offer." : "Select an offer"}
                    error={offerError}
                    disabled={isUploading || isDisabledAfterSuccess}
                />

                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                />

                {submissionError && (
                    <Typography color="error" sx={{ mt: 2, mb: 2, textAlign: "center" }}>
                        {submissionError}
                    </Typography>
                )}

                <Box display="flex" justifyContent="space-between" sx={{ mt: 3, mb: 3 }}>
                    <Button
                        variant="outlined"
                        onClick={handleDownloadTemplate}
                        disabled={isUploading || isDisabledAfterSuccess}
                        sx={{ flex: 1, mr: 1 }}
                    >
                        Download Template
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleUploadFile}
                        disabled={isUploading || isDisabledAfterSuccess}
                        sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}
                    >
                        {isUploading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : "Upload File"}
                        <FolderOpen sx={{ ml: 1 }} />
                    </Button>
                </Box>

                {blockedNumbersCount !== null && availableNumbersCount !== null && (
                    <TableContainer component={Paper} sx={{ borderRadius: "8px", boxShadow: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Category</TableCell>
                                    <TableCell>Count</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell>Blocked Numbers</TableCell>
                                    <TableCell>{blockedNumbersCount}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Available Numbers</TableCell>
                                    <TableCell>{availableNumbersCount}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
        </Dialog>
    );
};
